import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, Lock, ArrowRight, Mail } from "lucide-react";
import useSubscription from "@/hooks/useSubscription";
import {
  getSubscriptionStatus, setSubscriberEmail,
} from "@/lib/api";
import { toast } from "sonner";

/**
 * Gate that verifies the visitor holds an active £8.88/month membership
 * before allowing them to view a paid reading (Birth Chart or
 * Compatibility). Two supported usages:
 *
 *   1. Wrapper mode (default):
 *        <ReadingGate productLabel="birth chart reading">
 *          <YourReadingUI />
 *        </ReadingGate>
 *      Renders a check-splash / paywall around children. Works for pages
 *      with a single return path.
 *
 *   2. Explicit early-return mode (for pages with polling/loading early
 *      returns that would skip a wrapping component):
 *        const gate = useReadingGate("compatibility reading");
 *        if (gate) return gate;  // paywall or splash
 *        // …the rest of your component's returns are only reached when a
 *        // membership is active.
 */
export default function ReadingGate({ productLabel = "reading", children }) {
  const gateEl = useReadingGate(productLabel);
  if (gateEl) return gateEl;
  return children;
}

export function useReadingGate(productLabel = "reading") {
  const { active, loading } = useSubscription();
  if (loading) return <ReadingGateLoading />;
  if (!active) return <ReadingGatePaywall productLabel={productLabel} />;
  return null;
}

function ReadingGateLoading() {
  return (
    <div
      className="min-h-screen relative z-10 flex items-center justify-center px-6"
      data-testid="reading-gate-loading"
    >
      <div className="text-center">
        <div className="mx-auto w-12 h-12 rounded-full border border-[color:var(--gold)]/50 flex items-center justify-center animate-pulse">
          <Sparkles size={18} className="text-[color:var(--gold)]" />
        </div>
        <p className="mt-6 font-serif-italic text-[color:var(--parchment-soft)]">
          Checking your membership…
        </p>
      </div>
    </div>
  );
}

function ReadingGatePaywall({ productLabel }) {
  const location = useLocation();
  const [checkEmail, setCheckEmail] = useState("");
  const [checking, setChecking] = useState(false);

  const identifyMe = async () => {
    if (!/^\S+@\S+\.\S+$/.test(checkEmail)) {
      toast.error("Please enter a valid email");
      return;
    }
    setChecking(true);
    try {
      const r = await getSubscriptionStatus({ email: checkEmail });
      if (r?.active) {
        setSubscriberEmail(checkEmail);
        toast.success("Welcome back — unlocking your reading.");
        window.location.reload();
      } else {
        toast.error("No active membership for that email.", {
          description: "Start one below to unlock unlimited readings.",
        });
      }
    } catch {
      toast.error("Could not verify — please try again in a moment.");
    } finally {
      setChecking(false);
    }
  };

  const from = encodeURIComponent(location.pathname + location.search);
  return (
    <div className="min-h-screen relative z-10" data-testid="reading-gate-paywall">
      <header className="border-b border-[color:var(--gold)]/15">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <Sparkles size={14} className="text-[color:var(--gold)]" />
            <span className="label-overline">Earth Pulse</span>
          </Link>
          <span className="label-overline text-[color:var(--text-secondary)]">Members only</span>
        </div>
      </header>

      <main className="mx-auto max-w-[720px] px-6 md:px-10 py-16 md:py-24 text-center">
        <div className="mx-auto w-16 h-16 rounded-full border border-[color:var(--gold)]/40 flex items-center justify-center">
          <Lock size={22} className="text-[color:var(--gold)]" />
        </div>
        <div className="mt-6 label-overline text-[color:var(--gold)]">
          Members-only {productLabel}
        </div>
        <h1 className="mt-4 font-serif-display text-[color:var(--parchment)] text-4xl md:text-5xl leading-[1.05] tracking-tight">
          Add your <span className="font-serif-italic text-[color:var(--gold)]">£8.88/month</span> membership to open this reading.
        </h1>
        <p className="mt-6 font-serif-italic text-[color:var(--parchment-soft)] text-lg leading-relaxed max-w-lg mx-auto">
          Unlimited Birth Charts &amp; Compatibility readings, screenshot &amp; share as many as your heart wants, cancel any time.
        </p>

        <Link
          to={`/subscribe?from=${from}`}
          className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] text-lg font-medium transition"
          data-testid="reading-gate-subscribe"
        >
          <Sparkles size={16} /> Start membership · £8.88 / month <ArrowRight size={16} />
        </Link>

        <div className="mt-14 max-w-md mx-auto parchment-card rounded-2xl p-6 md:p-8" data-testid="reading-gate-identify">
          <div className="label-overline text-[color:var(--gold)]">Already subscribed?</div>
          <p className="mt-2 font-serif-italic text-[color:var(--text-secondary)] text-sm">
            Enter the email on your membership and we'll unlock your reading.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={checkEmail}
              onChange={(e) => setCheckEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") identifyMe(); }}
              placeholder="you@example.com"
              autoComplete="email"
              className="flex-1 bg-white border border-slate-300 rounded-lg h-11 px-3 text-slate-900 font-sans-body focus:outline-none focus:border-[color:var(--gold)] focus:ring-2 focus:ring-[color:var(--gold)]/40"
              data-testid="reading-gate-email-input"
            />
            <button
              type="button"
              onClick={identifyMe}
              disabled={checking}
              className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] disabled:opacity-60 text-[color:var(--bg-base)] font-medium px-5 transition"
              data-testid="reading-gate-verify"
            >
              <Mail size={14} />
              {checking ? "Checking…" : "Verify"}
            </button>
          </div>
        </div>

        <p className="mt-14 font-serif-italic text-[color:var(--text-muted)] text-xs">
          The daily guidance stays free. Readings live behind the membership so they're
          uncrowded and personal. Cancel any time — no hard feelings.
        </p>
      </main>
    </div>
  );
}
